import React from 'react';
import MediaComponent from '@minecraft.media-component';
import { MediaHelper } from '@blocs.common';
import { Media, MediaTypeCodeEnum, SubmissionMedia } from '../../graphql-operations';
import { BasicContainer, DetailTextLarge } from '../../atoms';
import { EditPrimaryStatus } from './EditPrimaryStatus';
import { DeleteMedia } from './DeleteMedia';
import { SelectMedia } from './SelectMedia';
import { SizeType } from '../../components/CloudinaryPhoto';

type ReadOnlyMediaItemProps = {
  mediaItem: Media | SubmissionMedia;
  onPreviewMediaClick?: (params?: { mediaItem: Media | SubmissionMedia }) => void;
  isPrimary?: boolean;
  disabled?: boolean;
  size?: SizeType;
  'data-qa-id'?: string;
  'data-testid'?: string;
};

type SelectableMediaItemProps = {
  isSelected: boolean;
  onSelectMediaItem: (params: { mediaItem: Media | SubmissionMedia }) => void;
  name: string;
  checkboxLabel: string;
  isSingleSelection?: boolean;
};
type UnselectableMediaItemProps = {
  isSelected?: never;
  onSelectMediaItem?: never;
  name?: never;
  checkboxLabel?: never;
  isSingleSelection?: never;
};
type SelectableProps = SelectableMediaItemProps | UnselectableMediaItemProps;

type EditablePrimaryStatusProps = {
  onChangePrimaryStatus: () => void;
};
type NonEditablePrimaryStatusProps = {
  onChangePrimaryStatus?: never;
};
type EditableProps = EditablePrimaryStatusProps | NonEditablePrimaryStatusProps;

type DeleteableMediaItemProps = {
  onDelete: (params: { mediaItem: Media | SubmissionMedia }) => void;
};
type NonDeleteableMediaItemProps = {
  onDelete?: never;
};
type DeletableProps = DeleteableMediaItemProps | NonDeleteableMediaItemProps;

export type InterativeMediaViewerProps = ReadOnlyMediaItemProps & SelectableProps & EditableProps & DeletableProps;

const AUDIO_CONTAINER_PROPS = ({ isSelected }: { isSelected: boolean }) => ({
  borderSize: 0.125,
  borderPlacement: 'all',
  borderColor: isSelected ? 'primary' : 'default',
  borderRadius: 'sm',
  className: 'cn_overflow-hidden cn_relative',
  padding: 1,
  bgColor: 'card',
});

const DEFAULT_CONTAINER_PROPS = ({ isSelected }: { isSelected: boolean }) => ({
  borderSize: 0.25,
  borderPlacement: 'all',
  borderColor: isSelected ? 'primary' : 'transparent',
  borderRadius: 'xl',
  className: 'cn_overflow-hidden cn_relative',
});

export const InterativeMediaViewer = ({
  size = 'md',
  name,
  disabled,
  isPrimary,
  mediaItem,
  isSelected,
  checkboxLabel,
  isSingleSelection,
  onDelete,
  onSelectMediaItem,
  onPreviewMediaClick,
  onChangePrimaryStatus,
  'data-qa-id': qaId,
  'data-testid': testId,
}: InterativeMediaViewerProps) => {
  const isAudio = mediaItem.mediaType.code === MediaTypeCodeEnum.Audio;
  const isSelectable =
    typeof isSelected === 'boolean' && typeof onSelectMediaItem === 'function' && typeof name === 'string';
  const isPrimaryStatusEditable = typeof onChangePrimaryStatus === 'function';
  const isDeletable = typeof onDelete === 'function';
  const isDisabled = isPrimary || disabled;
  return (
    <BasicContainer
      {...(isAudio ? AUDIO_CONTAINER_PROPS({ isSelected }) : DEFAULT_CONTAINER_PROPS({ isSelected }))}
      data-testid={testId}
      data-qa-id={qaId}
      w="max"
    >
      {isSelectable && (
        <SelectMedia
          mediaItem={mediaItem}
          isSelected={isSelected}
          onSelectMediaItem={onSelectMediaItem}
          isAudio={isAudio}
          disabled={isDisabled}
          name={name}
          checkboxLabel={checkboxLabel}
          isSingleSelection={isSingleSelection}
        />
      )}
      <MediaComponent
        media={mediaItem}
        type={size}
        data-qa-id={`customize-media-${mediaItem.mediaType.code.toLowerCase()}`}
        onClick={() => {
          onPreviewMediaClick?.({ mediaItem });
        }}
      />
      {isAudio && <DetailTextLarge>{MediaHelper.getMediaNameWithExtension(mediaItem)}</DetailTextLarge>}
      {isPrimaryStatusEditable && (
        <BasicContainer>
          <EditPrimaryStatus
            mediaItem={mediaItem}
            isPrimary={isPrimary}
            isSelected={isSelected}
            onChangePrimaryStatus={onChangePrimaryStatus}
          />
        </BasicContainer>
      )}
      {isDeletable && <DeleteMedia mediaItem={mediaItem} onDelete={onDelete} isAudio={isAudio} />}
    </BasicContainer>
  );
};
